import type {
  OpportunityFeedFilters,
  PaginatedRadarEvents,
  RadarEventDetail,
  RadarEventEvidence,
  RadarEventFilters,
  RadarLargeTradeMarketSection,
  RadarLiveOddsComparison,
  RadarWhaleOrder,
} from '~/features/opportunity-monitoring/types'

import { getOpportunity, listOpportunities } from '~/features/opportunity-monitoring/api'
import { apiClient, authApiClient } from '~/lib/api-client'

const liveOddsClient = authApiClient.api['live-odds']
const radarEventsClient = apiClient.api.radar.events
const authedRadarEventsClient = authApiClient.api.radar.events

export class ApiResponseError extends Error {
  readonly requestId: string | null
  readonly status: number
  readonly statusText: string

  constructor(response: Response, bodySnippet?: string) {
    const message = [
      `API request failed with ${response.status} ${response.statusText}`.trim(),
      bodySnippet ? `: ${bodySnippet}` : '',
    ].join('')

    super(message)
    this.name = 'ApiResponseError'
    this.status = response.status
    this.statusText = response.statusText
    this.requestId = response.headers.get('x-request-id')
  }
}

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const bodySnippet = await response.text().catch(() => '')
    throw new ApiResponseError(response, bodySnippet.slice(0, 500))
  }
  return response.json() as Promise<T>
}

export async function listRadarOpportunities(filters: OpportunityFeedFilters = {}) {
  return listOpportunities({
    ...filters,
    mappedOnly: true,
  })
}

export async function getRadarOpportunityDetail(opportunityId: string) {
  return getOpportunity(opportunityId)
}

export async function listRadarEvents(filters: RadarEventFilters = {}) {
  const response = await radarEventsClient.$get({
    query: filters,
    header: { Authorization: '' },
  })
  return parseJson<PaginatedRadarEvents>(response)
}

export interface WhaleOrderFilters {
  eventIds?: string
  polymarketEventId?: string
  polymarketMarketId?: string
  polymarketOutcomeId?: string
  clobTokenId?: string
  marketSection?: RadarLargeTradeMarketSection
  lineValue?: string
  minNotionalUsd?: number
  windowMinutes?: number
  limit?: number
}

export async function listWhaleOrders(query?: WhaleOrderFilters) {
  const response = await apiClient.api.radar['whale-orders'].$get({
    query: query ?? {},
    header: { Authorization: '' },
  })
  return parseJson<RadarWhaleOrder[]>(response)
}

export async function getRadarEventDetail(eventId: string) {
  const response = await authedRadarEventsClient[':eventId'].$get({
    param: { eventId },
    header: { Authorization: '' },
  })
  return parseJson<RadarEventDetail>(response)
}

export async function getRadarEventEvidence(eventId: string) {
  const response = await authedRadarEventsClient[':eventId'].evidence.$get({
    param: { eventId },
    header: { Authorization: '' },
  })
  return parseJson<RadarEventEvidence>(response)
}

export async function listRadarLiveOddsEvidence(input: {
  polymarketEventId: string
  polymarketMarketId?: string
  polymarketOutcomeId?: string
}) {
  const response = await liveOddsClient.radar.evidence.$get({
    query: input,
    header: { Authorization: '' },
  })
  return parseJson<RadarLiveOddsComparison[]>(response)
}
